import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Check, Rocket, MessageCircle, Shield, Zap, Users, FileText, 
  Bot, Sparkles, CreditCard, Loader2, CheckCircle, XCircle,
  Building2, Crown, Star
} from 'lucide-react';

// Plans data
const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Para autónomos y pequeños negocios',
    price: 29,
    currency: '€',
    period: '/mes',
    features: [
      { text: '1 usuario', included: true },
      { text: 'Todos los módulos', included: true },
      { text: '100 documentos/mes', included: true },
      { text: 'Soporte por email', included: true },
      { text: 'Exportación Excel/PDF', included: true },
      { text: 'MANDO IA', included: false },
      { text: 'Multi-usuario', included: false },
    ],
    popular: false,
    icon: Building2,
    color: 'slate'
  },
  {
    id: 'profesional',
    name: 'Profesional',
    description: 'Para equipos que quieren crecer',
    price: 79,
    currency: '€',
    period: '/mes',
    features: [
      { text: 'Hasta 5 usuarios', included: true },
      { text: 'Todos los módulos', included: true },
      { text: 'Documentos ilimitados', included: true },
      { text: 'Soporte prioritario', included: true },
      { text: 'MANDO IA incluido', included: true, highlight: true },
      { text: 'Roles y permisos', included: true },
      { text: 'API acceso', included: true },
    ],
    popular: true,
    icon: Crown,
    color: 'orange'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Para grupos y franquicias',
    price: null,
    priceText: 'Contactar',
    currency: '',
    period: '',
    features: [
      { text: 'Usuarios ilimitados', included: true },
      { text: 'Multi-empresa', included: true, highlight: true },
      { text: 'Panel Superadmin', included: true },
      { text: 'Soporte dedicado', included: true },
      { text: 'MANDO IA avanzado', included: true },
      { text: 'Integraciones', included: true },
      { text: 'SLA garantizado', included: true },
    ],
    popular: false,
    icon: Star,
    color: 'purple'
  }
];

const ActivarPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { companyName, updateTenant, fetchCapabilities } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('profesional');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');

    if (sessionId && status === 'success') {
      checkPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.error('Pago cancelado. Puedes intentarlo de nuevo cuando quieras.');
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setPaymentStatus('success');
          toast.success('¡Pago completado! Tu cuenta ha sido activada.');
          await fetchCapabilities();
          setTimeout(() => navigate('/'), 2000);
          return;
        }

        if (data.status === 'expired') {
          setPaymentStatus('error');
          toast.error('La sesión de pago ha expirado.');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setPaymentStatus('pending');
          toast.info('El pago está siendo procesado. Recibirás confirmación pronto.');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        setPaymentStatus('error');
      }
    };

    await poll();
    setCheckingPayment(false);
  };

  const handleCheckout = async (planId) => {
    if (planId === 'enterprise') {
      window.location.href = 'mailto:ventas@leapifyes.com?subject=MANDO%20Enterprise%20-%20Solicitud';
      return;
    }

    setLoading(true);
    setSelectedPlan(planId);

    try {
      const response = await api.post('/payments/checkout', {
        plan_id: planId,
        origin_url: window.location.origin
      });

      // Redirect to Stripe
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.code === 'STRIPE_NOT_CONFIGURED') {
        toast.error('El sistema de pagos aún no está configurado. Contacta con soporte.');
      } else if (detail?.code === 'ENTERPRISE_CONTACT') {
        toast.info(detail.message);
      } else {
        toast.error(detail?.message || detail || 'Error al iniciar el pago');
      }
      setLoading(false);
    }
  };

  // Payment status screen
  if (checkingPayment || paymentStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-6">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-12 pb-8">
            {checkingPayment && (
              <>
                <Loader2 className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-900 mb-2">Verificando pago...</h2>
                <p className="text-slate-500">Por favor, espera mientras confirmamos tu pago.</p>
              </>
            )}
            {paymentStatus === 'success' && (
              <>
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">¡Pago completado!</h2>
                <p className="text-slate-500 mb-4">Tu cuenta ha sido activada correctamente.</p>
                <p className="text-sm text-slate-400">Redirigiendo al dashboard...</p>
              </>
            )}
            {paymentStatus === 'error' && (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">Error en el pago</h2>
                <p className="text-slate-500 mb-4">Hubo un problema con tu pago. Inténtalo de nuevo.</p>
                <Button onClick={() => setPaymentStatus(null)}>Volver a intentar</Button>
              </>
            )}
            {paymentStatus === 'pending' && (
              <>
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-10 h-10 text-yellow-600 animate-spin" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">Pago en proceso</h2>
                <p className="text-slate-500 mb-4">Tu pago está siendo procesado. Recibirás un email de confirmación.</p>
                <Button onClick={() => navigate('/')}>Ir al dashboard</Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 py-12 px-4" data-testid="activar-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Activa MANDO para {companyName}
          </h1>
          <p className="text-slate-400 text-lg">
            Elige el plan que mejor se adapte a tu negocio
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            const isSelected = selectedPlan === plan.id;
            const isPopular = plan.popular;

            return (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-300 ${
                  isPopular 
                    ? 'border-2 border-orange-500 shadow-xl shadow-orange-500/20 scale-105' 
                    : 'border border-slate-700 hover:border-slate-500'
                } ${isSelected ? 'ring-2 ring-orange-500' : ''}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    MÁS POPULAR
                  </div>
                )}
                
                <CardHeader className={`pb-4 ${isPopular ? 'bg-gradient-to-br from-orange-500/10 to-transparent' : ''}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isPopular ? 'bg-orange-500' : 'bg-slate-700'
                    }`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-white">{plan.name}</CardTitle>
                      <CardDescription className="text-slate-400">{plan.description}</CardDescription>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    {plan.price !== null ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-white">{plan.price}</span>
                        <span className="text-slate-400">{plan.currency}{plan.period}</span>
                      </div>
                    ) : (
                      <span className="text-2xl font-bold text-white">{plan.priceText}</span>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        {feature.included ? (
                          <Check className={`w-5 h-5 ${feature.highlight ? 'text-orange-500' : 'text-green-500'}`} />
                        ) : (
                          <div className="w-5 h-5 flex items-center justify-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-slate-600" />
                          </div>
                        )}
                        <span className={`text-sm ${
                          feature.included 
                            ? feature.highlight ? 'text-orange-400 font-medium' : 'text-slate-300'
                            : 'text-slate-500'
                        }`}>
                          {feature.text}
                          {feature.highlight && feature.text.includes('IA') && (
                            <Sparkles className="w-3 h-3 inline ml-1 text-yellow-400" />
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleCheckout(plan.id)}
                    disabled={loading && selectedPlan === plan.id}
                    className={`w-full ${
                      isPopular 
                        ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                        : plan.id === 'enterprise'
                          ? 'bg-purple-600 hover:bg-purple-700 text-white'
                          : 'bg-slate-700 hover:bg-slate-600 text-white'
                    }`}
                  >
                    {loading && selectedPlan === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Procesando...
                      </>
                    ) : plan.id === 'enterprise' ? (
                      <>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Contactar Ventas
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Seleccionar Plan
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center gap-6 text-slate-400 text-sm">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span>Pago seguro con Stripe</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            <span>Cancela cuando quieras</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            <span>Activación inmediata</span>
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-8">
          <button 
            onClick={() => navigate('/')}
            className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
          >
            ← Volver al dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivarPage;
